import React, {ComponentType, lazy, Suspense} from 'react';
import {View} from 'react-native';

import {logger, serializeError} from '../../utils/logger';

type Size = {width: number; height: number};

/**
 * R63: the mega menu promo artwork (the rewards banner SVGs for 4 brands and the 2 promo photos)
 * loads on first render of a promo tile, in its own web chunk, not with every page. Until it
 * arrives the tile holds an empty box of the same size, so the layout does not shift.
 */
const EmptyBox = ({width, height}: Size) => (
  <View testID="site-header-promo-fallback" style={{width, height}} />
);

/**
 * A failed chunk load (offline on first open, or a deploy that removed the old chunk) resolves
 * to the empty box instead of rejecting: a rejected lazy import throws on render, and the next
 * boundary up is the app-wide one, which replaces the whole app. React.lazy caches the result,
 * so each artwork logs its failure once.
 */
const lazyArt = (name: string, load: () => Promise<ComponentType<Size>>): ComponentType<Size> => {
  const Art = lazy(() =>
    load()
      .then(Component => ({default: Component}))
      .catch((err: unknown) => {
        logger.warn(
          'SiteHeader',
          `Promo art "${name}" failed to load; the tile stays blank`,
          serializeError(err),
        );
        return {default: EmptyBox};
      }),
  );
  const LazyArt = ({width, height}: Size) => (
    <Suspense fallback={<EmptyBox width={width} height={height} />}>
      <Art width={width} height={height} />
    </Suspense>
  );
  return LazyArt;
};

export const LazyRewardsPromoBanner = lazyArt('rewards banner', () =>
  import('./RewardsBanner').then(m => m.RewardsPromoBanner),
);
export const LazyPromoNew = lazyArt('New', () =>
  import('./promoPhotos').then(m => m.PromoNewPhoto),
);
export const LazyPromoChangeOfPlans = lazyArt('Change of plans', () =>
  import('./promoPhotos').then(m => m.PromoChangeOfPlansPhoto),
);
